// Theme (dark/light, persisted) + a tiny toast bus. Both ported from the
// Claude-Design prototype's interactions.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const ThemeCtx = createContext({ theme: "dark", toggle: () => {} });
const ToastCtx = createContext({ notify: () => {} });
const ValueModeCtx = createContext({ mode: "abs", set: () => {} });

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute("data-theme") || "dark"
  );
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try { localStorage.setItem("cc-theme", theme); } catch {}
  }, [theme]);
  const toggle = useCallback(() => setTheme((t) => (t === "dark" ? "light" : "dark")), []);
  const value = useMemo(() => ({ theme, toggle }), [theme]);   // stabilize identity (toggle is already stable)
  return <ThemeCtx.Provider value={value}>{children}</ThemeCtx.Provider>;
}
export const useTheme = () => useContext(ThemeCtx);

export function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const n = useRef(0);
  const notify = useCallback((msg) => {
    const id = ++n.current;
    setToast({ msg, id });
    setTimeout(() => setToast((t) => (t && t.id === id ? null : t)), 2600);
  }, []);
  const value = useMemo(() => ({ notify }), [notify]);   // stabilize identity (notify is already useCallback)
  return (
    <ToastCtx.Provider value={value}>
      {children}
      {toast && (
        <div className="toast" role="status">
          <span className="dot" />
          <span>{toast.msg}</span>
        </div>
      )}
    </ToastCtx.Provider>
  );
}
export const useToast = () => useContext(ToastCtx);

// Value mode for growth charts: "abs" ($) or "ret" (cumulative return %), persisted.
export function ValueModeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("cc-valmode") || "abs"; } catch { return "abs"; }
  });
  useEffect(() => { try { localStorage.setItem("cc-valmode", mode); } catch {} }, [mode]);
  const value = useMemo(() => ({ mode, set: setMode }), [mode]);   // stabilize identity (setMode is a stable setter)
  return <ValueModeCtx.Provider value={value}>{children}</ValueModeCtx.Provider>;
}
export const useValueMode = () => useContext(ValueModeCtx);

// F2 (resource-state contract): a TRANSPORT/SERVER failure is its own state — never "disconnected
// broker", never an eternal skeleton, never a silent empty panel. One component, used by every
// money surface: says what failed, that the book itself is unchanged, and offers Retry.
export function LoadError({ what = "this data", error, onRetry }) {
  return (
    <section className="panel load-error" role="alert">
      <p className="muted" style={{ margin: 0 }}>
        Couldn't load {what} — {error?.status ? `the server answered HTTP ${error.status}` : "the server is unreachable"}.
        This is a read failure, not a broker or account problem; nothing about your book changed.
      </p>
      {onRetry && <button className="btn btn--ghost btn--xs" style={{ marginTop: 8 }} onClick={onRetry}>↻ Retry</button>}
    </section>
  );
}
